/**
 * Routes one SSE connection to the assistant turn it owns.
 *
 * Older API deployments do not emit assistant_turn_started or attach an id to
 * frames. Those streams retain their optimistic client message id. Once the
 * new first frame arrives, every later frame must name the same turn; accepting
 * an untagged or foreign frame after that point could render another request's
 * output in this turn.
 */
export interface AssistantTurnFrame {
  type: string;
  assistant_turn_id?: string;
}

export interface AssistantTurnRoute {
  messageId: string;
  accepted: boolean;
  started: boolean;
}

export interface AssistantTurnRouter {
  route(frame: AssistantTurnFrame): AssistantTurnRoute;
  messageId(): string;
}

export function createAssistantTurnRouter(
  optimisticMessageId: string,
): AssistantTurnRouter {
  let activeTurnId: string | null = null;
  let activeMessageId = optimisticMessageId;

  return {
    route(frame) {
      if (frame.type === "assistant_turn_started") {
        if (!frame.assistant_turn_id) {
          return {
            messageId: activeMessageId,
            accepted: false,
            started: false,
          };
        }
        if (activeTurnId && activeTurnId !== frame.assistant_turn_id) {
          return {
            messageId: activeMessageId,
            accepted: false,
            started: false,
          };
        }
        const started = activeTurnId === null;
        activeTurnId = frame.assistant_turn_id;
        activeMessageId = frame.assistant_turn_id;
        return { messageId: activeMessageId, accepted: true, started };
      }

      // Before the start frame, only legacy untagged events are compatible.
      // The backend contract makes a tagged pre-start event invalid for this
      // connection, so dropping it is safer than assigning it speculatively.
      if (activeTurnId === null) {
        return {
          messageId: activeMessageId,
          accepted: frame.assistant_turn_id === undefined,
          started: false,
        };
      }

      return {
        messageId: activeMessageId,
        accepted: frame.assistant_turn_id === activeTurnId,
        started: false,
      };
    },
    messageId: () => activeMessageId,
  };
}
